import React, { useState, useEffect, useRef } from "react";
import "./ImageCarousel.css";
import "../../User/AboutUs/AboutUs.css";

const ImageCarousel = ({
  slides,
  autoPlayInterval = 3000, // Reduced time interval
  showOverlay = true,
  overlayContent = [],
  showNavButtons = true,
  showIndicators = true,
  height = "500px",
  width = "100%",
  aspectRatio = null,
  zoom = true,
  zoomDuration = 5000, // Reduced zoom duration
  specificClassName,
  bShape,
  disableAutoPlay = false,
  hideNav = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [overlayKey, setOverlayKey] = useState(0); // For triggering overlay animation
  const intervalId = useRef(null);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setOverlayKey((prevKey) => prevKey + 1); // Reset overlay animation
    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides?.length);
      setIsTransitioning(false);
    }, 500); // Short transition between slides
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setOverlayKey((prevKey) => prevKey + 1); // Reset overlay animation
    setTimeout(() => {
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + slides?.length) % slides?.length
      );
      setIsTransitioning(false);
    }, 500); // Short transition between slides
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setOverlayKey((prevKey) => prevKey + 1); // Reset overlay animation
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 500);
  };

  useEffect(() => {
    if (!disableAutoPlay && autoPlayInterval > 0) {
      intervalId.current = setInterval(nextSlide, autoPlayInterval);
      return () => clearInterval(intervalId.current);
    }
  }, [autoPlayInterval, disableAutoPlay]);

  const pauseAutoPlay = () => {
    if (intervalId.current) clearInterval(intervalId.current);
  };

  const resumeAutoPlay = () => {
    if (autoPlayInterval > 0) {
      intervalId.current = setInterval(nextSlide, autoPlayInterval);
    }
  };

  const containerStyle = {
    height: height,
    width: width,
    ...(aspectRatio && { aspectRatio: aspectRatio }),
    position: "relative",
    overflow: "hidden",
  };

  if (!slides) return null;

  return (
    <>
      <div
        className={`zoom-carousel-container ${specificClassName}`}
        style={containerStyle}
        onMouseEnter={pauseAutoPlay}
        onMouseLeave={resumeAutoPlay}
      >
        {/* Slides */}
        {slides?.map((slide, index) => (
          <div
            key={index}
            className={`zoom-carousel-slide ${index === currentIndex ? "active" : ""
              }`}
          >
            <div
              className="zoom-effect"
              style={{
                backgroundImage:
                  typeof slide === "string" ? `url(${slide})` : "",
                transform: zoom && (index === currentIndex) ? "scale(1.1)" : "scale(1)",
                transition: zoom
                  ? `transform ${zoomDuration}ms ease-in-out`
                  : "",
              }}
            >
              {typeof slide !== "string" && slide}
            </div>
          </div>
        ))}

        {/* Overlay - Now showing on every slide */}
        {showOverlay && (
          <div
            key={overlayKey} // Key changes trigger re-render and animation
            className="zoom-carousel-overlay"
          >
            {overlayContent[currentIndex]}
          </div>
        )}
        {/* Navigation Buttons */}
        {showNavButtons && hideNav && (
          <>
            <button
              className="zoom-carousel-nav prev-button btn  btn-outline-light rounded-circle shadow"
              onClick={prevSlide}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <button
              className="zoom-carousel-nav next-button btn  btn-outline-light rounded-circle shadow"
              onClick={nextSlide}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </>
        )}

        {/* Indicators */}
        {showIndicators && (
          <div className="zoom-carousel-indicators">
            {slides?.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentIndex ? "active" : ""
                  }`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      {bShape && <div className="main-slider-four__bg-shape"></div>}
    </>
  );
};

export default ImageCarousel;
